import React, { useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Loader2 } from "lucide-react";
import { API, auth, buyTopup, fmtDay, switchArea, switchError } from "../../components/membership/membershipApi";

const LINKS = { golf: [["/golf", "Open Golf"], ["/golf/coach", "Golf Coach"]], gym: [["/gym", "Open Gym"], ["/gym/guide", "Gym Coach"]] };

const Level = ({ id, title, price, blurb, current, onPick, busy, coach }) => (
  <div className={`rounded-2xl border p-5 transition-colors ${current ? "border-[#d7bb7a] bg-[#d7bb7a]/10" : "border-[#3a3f35] bg-[#141a17]"}`} data-testid={`membership-level-${id}`}>
    <div className="flex items-baseline justify-between gap-3">
      <h2 className="text-2xl text-[#f3ecd9]" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>{title}</h2>
      <p className="text-lg text-[#f3ecd9]">£{price.toFixed(2)}<span className="text-xs text-[#b9ad8f]"> /mo</span></p>
    </div>
    <p className="mt-1 text-sm text-[#cfc6ae]">{blurb}</p>
    {coach && (
      <p className="mt-2 text-xs text-[#9fb39a]" data-testid={`membership-coach-${id}`}>
        {coach.unlimited ? "Coach: unlimited (team account)" : `${Math.floor(coach.left)} of ${coach.included} Coach minutes left · renews ${fmtDay(coach.renews_on)}`}
        {coach.topup_left > 0 && ` · includes ${Math.floor(coach.topup_left)} extra`}
      </p>
    )}
    {current ? (
      <div className="mt-4 flex flex-wrap items-center gap-4">
        <p className="inline-flex items-center gap-1.5 text-xs uppercase tracking-[0.22em] text-[#d7bb7a]" data-testid={`membership-current-${id}`}><Check size={13} /> Your level</p>
        {(LINKS[id] || []).map(([to, l]) => <a key={to} href={to} className="text-xs text-[#f3ecd9] underline underline-offset-4" data-testid={`membership-open-${to.replace(/\//g, "-").slice(1)}`}>{l}</a>)}
      </div>
    ) : (
      <button type="button" onClick={() => onPick(id)} disabled={busy} data-testid={`membership-switch-${id}`}
        className="mt-4 rounded-full border border-[#d7bb7a] px-5 py-2.5 text-xs uppercase tracking-[0.22em] text-[#f3ecd9] transition-colors hover:bg-[#d7bb7a]/20 disabled:opacity-50">
        {busy ? <Loader2 size={13} className="animate-spin" /> : `Switch to ${title}`}
      </button>
    )}
  </div>
);

export default function MembershipPage() {
  const [s, setS] = useState(null);
  const [busy, setBusy] = useState(false);
  const load = () => axios.get(`${API}/api/membership/state`, auth()).then((r) => setS(r.data)).catch(() => setS({ error: true }));
  useEffect(() => {
    const sid = new URLSearchParams(window.location.search).get("session_id");
    if (sid) axios.get(`${API}/api/voice-clone/checkout/status/${sid}`, auth()).finally(load);
    else load();
  }, []);
  const pick = async (area) => {
    setBusy(true);
    try { const r = await switchArea(area); if (r.switched) { toast.success("Your membership level is updated"); load(); } }
    catch (e) { toast.error(switchError(e)); }
    setBusy(false);
  };
  if (!s) return <div className="flex min-h-screen items-center justify-center bg-[#0f1412]"><Loader2 className="animate-spin text-[#d7bb7a]" /></div>;
  if (s.error) return <div className="min-h-screen bg-[#0f1412] p-10 text-center text-[#cfc6ae]">Couldn't load your membership.</div>;
  const current = s.core_active ? (s.area || "core") : null;
  const anyCoach = Object.values(s.areas).find((a) => a.coach);
  return (
    <div className="min-h-screen bg-[#0f1412] px-5 py-12 pb-32" data-testid="membership-page">
      <div className="mx-auto max-w-xl">
        <p className="text-xs uppercase tracking-[0.3em] text-[#d7bb7a]">Membership</p>
        <h1 className="mt-3 text-4xl text-[#f3ecd9] sm:text-5xl" style={{ fontFamily: '"Cormorant Garamond", Georgia, serif' }}>Choose your level</h1>
        <p className="mt-3 text-sm text-[#cfc6ae]">Switch any time. Stripe only charges or credits the difference for the rest of the month.</p>
        <div className="mt-8 space-y-4">
          <Level id="core" title="Soulumo Core" price={s.core_price} blurb="Every Soulumo installation, made personal in your voice." current={current === "core"} onPick={pick} busy={busy} />
          {Object.entries(s.areas).map(([id, a]) => (
            <Level key={id} id={id} title={a.label} price={a.price} current={current === id} onPick={pick} busy={busy} coach={a.coach}
              blurb={`Everything in Core, plus every ${a.label.replace("Soulumo + ", "")} installation and monthly Coach time.`} />
          ))}
        </div>
        {anyCoach && s.topup?.active && (
          <button type="button" onClick={() => buyTopup("/soulumo/membership").catch(() => toast.error("Couldn't open checkout."))} data-testid="membership-topup-btn"
            className="mt-6 text-xs text-[#d7bb7a] underline underline-offset-4">Need more Coach time? Add {s.topup.minutes} minutes for £{(s.topup.price_pence / 100).toFixed(2)}</button>
        )}
        {s.premium_voice && <p className="mt-6 text-xs text-[#b9ad8f]" data-testid="membership-premium-note">You're on My Voice Premium. Message us to add Golf or Gym.</p>}
      </div>
    </div>
  );
}
